import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import $ from "jquery";
import { State } from '../services/global-state';
import { PayrazrService } from '../services/payrazr';
import { I18N_EA_SIGNAL } from './app';
import { DialogService } from 'aurelia-dialog';
import { LegalTermsDialog } from '../components/legal-dialog/legal-terms.dialog';
import { LegalPrivacyPolicyDialog } from '../components/legal-dialog/legal-privacypolicy.dialog';
import { LegalDisclosuresDialog } from '../components/legal-dialog/legal-disclosures.dialog';

declare var recaptchaToken: any; // this is set in index.cshtml, before aurelia loads

@autoinject
export class Portal {
	router: Router;

	logoText: string;
	useLogo: boolean; 
	logoUrl: string;
	year: number;

	constructor(
		private payrazr: PayrazrService,
		private globalState: State,
		private eventAggregator: EventAggregator,
		private i18n: I18N,
		private dialogService: DialogService
		) {
		this.year = new Date().getFullYear();
	}

	configureRouter(config: any, router: Router) {
		config.map([
			{ route: 'login', name: 'login', moduleId: './login-register/login', title: 'Login' },
			{ route: 'forgotPassword', name: 'forgotPassword', moduleId: './login-register/forgot-password', title: 'Forgot Password' },
			{ route: 'change-password', name: 'changePassword', moduleId: './login-register/change-password', title: 'Change Password' },
			{ route: 'register', name: 'register', moduleId: './login-register/register', title: 'Register' },
			{ route: 'verification', name: 'verification', moduleId: './login-register/sms-verification/sms-verification', title: 'verification', nav: true },
			{ route: 'preferredLanguage', name: 'preferredLanguage', moduleId: './preferred-language/preferred-language', title: 'Preferred Language', nav: true },
			{ route: 'setupPrimaryPaymentMethod', name: 'setupPrimaryPaymentMethod', moduleId: './login-register/setup-primary-payment-method/setup-primary-payment-method', title: 'Setup Primary Payment Method', nav: false },
			{ route: 'ezpay', name: 'ezpay', moduleId: './ezpay/ezpay', title: 'EZ Pay' },
			{ route: 'secure', name: 'secure', moduleId: './secure-index', title: 'Dashboard', nav: true, settings: { auth: true } },
			{ route: '401', name: '401', moduleId: './http-status/401', title: '401' },
			{ route: 'inactive', name: 'inactive', moduleId: './login-register/inactive', title: 'Inactive Portal' },
			{ route: 'error', name: 'error', moduleId: './error/error', title: 'An Error Occurred' },
			{ route: 'logout', name: 'logout', moduleId: './login-register/logout' },
			{ route: '', moduleId: './login-register/portal-waiting' }
		]);
		config.mapUnknownRoutes('./http-status/404');

		this.router = router;
	}

	attached() {
		if (this.globalState && this.globalState.business) {
			// When the page is manually refreshed then these style overrides disappear. 
			// This puts them back - if the portal has been loaded. 
			if (this.globalState.business.portalSettings.styles) {
				var portalStyles = $('#portalStyles');
				portalStyles.remove();

				$("body").append(this.globalState.business.portalSettings.styles);
			}

			if (this.globalState.business.portalSettings) {
				this.logoText = this.globalState.business.portalSettings.footerText;
				this.useLogo = this.globalState.business.portalSettings.hasLogo;
				this.logoUrl = this.globalState.business.portalSettings.logoImageUrl;
			}
		}
	}

	activate(params) {
		// Verify Access to SessionStorage, display error if access is denied
		try {
			let storage = window.sessionStorage;
			storage.setItem('test', 'test');
			storage.removeItem('test');
		} catch (e) {
			this.router.routes.find((routeConfig) => routeConfig.name === 'error').settings.type = 'SessionStorageError';
			this.router.navigateToRoute('error');
			return;
		}

		let portalId = params.id;
		if (params.id.length < 7) {//short portal id navigation
			return this.payrazr.getPortalId(portalId)
				.then(response => {
					if (response.isSuccessful) {
						window.location = response.entity; // response.entity contains the portal guid
					}
				})
				.catch((e) => {
					this.router.navigateToRoute('inactive');
				});
		}
		else {
			if (!this.globalState.business) {
				return this.payrazr.getPortal(portalId, recaptchaToken)
					.then(response => {
						if (!response)
							this.router.navigateToRoute('inactive');

						// getPortal sets up globalState.business
						if (this.globalState.business) {
							// setup default business in i18n
							this.i18n.i18next.loadNamespaces(this.globalState.business.key.toString(), (err) => {
                                this.eventAggregator.publish(I18N_EA_SIGNAL);
							});
							this.i18n.i18next.setDefaultNamespace(this.globalState.business.key.toString());
						}
						else {
                            this.eventAggregator.publish(I18N_EA_SIGNAL);
						}
					})
					.catch((e) => {
						this.router.navigateToRoute('inactive');
					});
			}
		}
	}
	//TODO: Chris - add computedFrom
	get csPhone() {
		return this.globalState.business && this.globalState.business.portalSettings
			? this.globalState.business.portalSettings.csPhone
			: "";
	}
	//TODO: Chris - add computedFrom
	get csEmail() {
		return this.globalState.business && this.globalState.business.portalSettings
			? this.globalState.business.portalSettings.csEmail
			: "";
	}

	toggleCiMode(e: any) {
		e = e || window.event;
		if (e.altKey && e.ctrlKey) {
			console.log('Toggling CI Mode');

			let currentLanguage = this.i18n.getLocale();
			let nextLanguage = 'en';
			if (currentLanguage == 'cimode') {				
				this.i18n.i18next.options.skipTranslationOnMissingKey = true;
			}
            else {
                // skipTranslationOnMissingKey is required for fallback text to work but it 
				// also breaks cimode from working. We'll need to set it true/false depending 
				// on whether we're in cimode or not.
				this.i18n.i18next.options.skipTranslationOnMissingKey = false; 
				nextLanguage = 'cimode';
			}

			this.i18n.setLocale(nextLanguage);
		}
	}

	showLegalModal(content:string) {
		if (content === 'terms') {
			this.dialogService.open({ viewModel: LegalTermsDialog, lock: false });
		} else if (content === 'privacyPolicy') {
			this.dialogService.open({ viewModel: LegalPrivacyPolicyDialog, lock: false });
		} else if (content === 'disclosures') {
			this.dialogService.open({ viewModel: LegalDisclosuresDialog, lock: false });
		}
		
	}
}
